/* NÜR keeps its original opening. XERF then appears in the same reserved stage. */
(() => {
  const story = document.querySelector('.nx-has-xerf');
  const stage = story?.querySelector('.nx-home-xerf');
  const video = stage?.querySelector('[data-home-xerf-video]');
  if (!story || !stage || !video) {
    document.documentElement.classList.remove('nx-campaign-pending');
    return;
  }
  const es = document.documentElement.lang === 'es';
  const toggle = stage.querySelector('[data-home-xerf-toggle]');
  const sound = stage.querySelector('[data-home-xerf-sound]');
  const ambient = stage.querySelector('[data-home-xerf-ambient]');
  const status = stage.querySelector('[data-home-xerf-status]');
  const reduceVideo = window.nanaMotion || matchMedia('(prefers-reduced-motion: reduce)');
  let revealed = false;
  let autoplayWanted = false;
  let autoplayStopped = false;
  let playPending = false;
  let pageActive = true;
  let lifecycle = 0;
  let fallback;
  let animationFallback;
  const clearFallback = () => {
    clearTimeout(fallback);
    animationFallback?.kill();
  };
  const visible = () => {
    const r = stage.getBoundingClientRect();
    const height = Math.min(r.bottom - r.top, innerHeight);
    // Restored scroll positions can leave the film visible below the first 24px.
    return pageActive && !document.hidden && height > 0 &&
      Math.min(r.bottom, innerHeight) - Math.max(r.top, 0) >= height * .5;
  };
  const label = () => {
    const text = video.paused ? (es ? 'Reproducir video' : 'Play video') : (es ? 'Pausar video' : 'Pause video');
    toggle.querySelector('[data-home-xerf-toggle-label]').textContent = text;
    toggle.setAttribute('aria-label', text);
    toggle.title = text;
    toggle.dataset.state = video.paused ? 'paused' : 'playing';
    const muted = video.muted || video.volume === 0;
    const soundText = muted ? (es ? 'Activar sonido' : 'Turn sound on') : (es ? 'Silenciar video' : 'Mute video');
    sound.querySelector('[data-home-xerf-sound-label]').textContent = soundText;
    sound.setAttribute('aria-label', soundText);
    sound.setAttribute('aria-pressed', String(!muted));
    sound.title = soundText;
    sound.dataset.state = muted ? 'muted' : 'audible';
  };
  const syncAmbient = () => {
    if (!ambient) return;
    ambient.muted = true;
    ambient.volume = 0;
    ambient.playbackRate = video.playbackRate;
    if (ambient.readyState > 0 && Math.abs(ambient.currentTime-video.currentTime) > .3) {
      try { ambient.currentTime = video.currentTime; } catch {}
    }
    if (video.paused || video.ended || document.hidden) ambient.pause();
    else if (ambient.paused) ambient.play().catch(() => {});
  };
  const pauseAll = () => { video.pause(); ambient?.pause(); };
  const suspend = () => { lifecycle++; pauseAll(); };
  const maybeStart = () => {
    if (revealed && autoplayWanted && !autoplayStopped && !playPending &&
        video.paused && !reduceVideo.matches && visible()) play(false);
  };
  const play = async manual => {
    if (playPending) return;
    playPending = true;
    const token = lifecycle;
    if (manual) autoplayStopped = true;
    if (manual && video.error) video.load();
    if (video.ended) video.currentTime = 0;
    if (!manual) video.muted = true;
    status.textContent = '';
    try {
      await video.play();
      if (token === lifecycle && !visible()) suspend();
    } catch {
      if (token === lifecycle) {
        // A real policy/media rejection is terminal for autoplay. The existing
        // play button remains available; scrolling is never a workaround.
        autoplayStopped = true;
        if (manual) status.textContent = es ? 'No se pudo reproducir. Inténtalo de nuevo.' : 'Playback was unavailable. Please try again.';
      }
    } finally {
      playPending = false;
    }
    label();
    // An offscreen/background cancellation may settle after we became visible.
    if (token !== lifecycle) maybeStart();
  };
  const reveal = ({autoplay=false, immediate=false} = {}) => {
    autoplayWanted ||= autoplay;
    if (!revealed) {
      revealed = true;
      clearFallback();
      clearTimeout(window.nanaCampaignFallback);
      document.documentElement.classList.remove('nx-campaign-pending');
      if (immediate) story.dataset.xerfImmediate = '';
      story.dataset.xerfPhase = 'featured';
      stage.inert = false;
      if (document.activeElement === story.querySelector('.nx-story-scroll')) {
        stage.querySelector('a[href="#nx-main"]')?.focus({preventScroll:true});
      }
    }
    maybeStart();
  };
  toggle.hidden = false;
  sound.hidden = false;
  toggle.addEventListener('click', () => {
    autoplayStopped = true;
    if (video.paused) play(true);
    else suspend();
  });
  sound.addEventListener('click', () => {
    video.muted = !video.muted;
    if (!video.muted && video.volume === 0) video.volume = 1;
    label();
  });
  ['play','pause'].forEach(type => video.addEventListener(type,label));
  ['play','pause','seeking','seeked','timeupdate','ratechange'].forEach(type => video.addEventListener(type,syncAmbient));
  video.addEventListener('volumechange',label);
  ambient?.addEventListener('loadedmetadata',syncAmbient);
  // Keep screen-text cues clear of the corner controls, including portrait screens.
  const placeCues = () => {
    for (const track of video.textTracks) for (const cue of track.cues || []) {
      cue.snapToLines = false; cue.line = 76;
      cue.size = 88; cue.position = 50; cue.positionAlign = 'center';
    }
  };
  video.querySelectorAll('track').forEach(track => track.addEventListener('load',placeCues));
  video.addEventListener('ended', () => {
    autoplayStopped = true;
    // The supplied film ends on a transition frame. Rest at its original poster,
    // without looping or starting another play request; controls can replay it.
    video.load();
    ambient?.pause();
    ambient?.load();
    label();
  });
  video.addEventListener('error', () => { label(); });
  const interrupt = () => {
    if (scrollY > 24) reveal({autoplay:true, immediate:true});
    if (!visible()) suspend();
    else maybeStart();
  };
  story.dataset.xerfPhase = 'opening';
  stage.inert = true;
  document.addEventListener('nana:intro-complete', () => reveal({autoplay:true, immediate:reduceVideo.matches}));
  addEventListener('scroll', interrupt, {passive:true});
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) suspend();
    else maybeStart();
  });
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => {
      if (!entries[0].isIntersecting || !visible()) suspend();
      else maybeStart();
    }, {threshold:[0,.5]});
    observer.observe(stage);
  }
  reduceVideo.addEventListener('change', () => {
    if (reduceVideo.matches) { autoplayStopped = true; suspend(); reveal({immediate:true}); }
  });
  addEventListener('pagehide', () => { pageActive = false; suspend(); clearFallback(); });
  addEventListener('pageshow', event => {
    pageActive = true;
    if (event.persisted) reveal({autoplay:true, immediate:true});
  });
  // Use the opening's animation clock when available: background throttling or
  // slow frames must not let a wall-clock watchdog hide the letters early.
  const armFallback = () => {
    if (revealed) return;
    if (window.gsap && window.ScrollTrigger && !reduceVideo.matches) {
      animationFallback = window.gsap.delayedCall(5.4, () => reveal({autoplay:true}));
    } else {
      fallback = setTimeout(() => reveal({autoplay:false}), 5400);
    }
  };
  // Deferred journey execution precedes DOMContentLoaded, including a slow
  // first download. Start the fallback after that script had its chance to run.
  if (document.readyState === 'complete') armFallback();
  else document.addEventListener('DOMContentLoaded', armFallback, {once:true});
  if (story.dataset.nanaIntro === 'complete' || scrollY > 24) reveal({autoplay:true, immediate:true});
  label();
  // Only replace native transport controls after all custom controls are wired.
  video.controls = false;
  stage.dataset.playerEnhanced = '';
  // This enhancement now owns its fallback. The head watchdog still protects
  // missing or failed execution of this file.
  clearTimeout(window.nanaCampaignFallback);
})();
